import { LitElement, css, html, nothing, unsafeCSS } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import sheet from './css/bb-chat-scheduled-time.css?inline'
import type { ScheduledTimeConfirmHint } from './bbcore/types'

// Renders a `confirmation_needed:scheduled_time` hint as a Day + Time
// picker. Symmetric to bb-chat-fulfillment / bb-chat-contact: dedicated
// custom element with inline/overlay layouts and shadow parts for host
// re-skinning. Trello #131 (was an asymmetry — the only blocking
// precondition hint without an extracted component or overlay path).
//
// On Schedule:
//   1. dispatches `schedule-order {iso}` (composed) — <bb-widget> catches
//      it and sends the same plain utterance ("Schedule it for <iso>")
//      the inline path used. The agent's schedule_order tool validates.
//   2. emits `widget-affordance {kind: "scheduled-time-pick", iso}` for
//      telemetry parity with the prior inline render.
//
// On Decline: dispatches `schedule-decline` (composed) + the matching
// affordance event. <bb-widget> sends "No thanks, not this time." so
// the agent acknowledges and the customer can keep shopping.
//
// Day / Time options are client-generated convenience ranges (next 7
// days + 30-min slots) defaulting to the hint's `suggested_iso`. They
// are a shortcut, not a validity claim — the agent's schedule_order
// tool validates on submit. (Trello #130 tracks teaching the dropdown
// about closed days from server state.)

const pad2 = (n: number) => String(n).padStart(2, '0')

const dayLabel = (yyyymmdd: string): string => {
  // The yyyy-mm-dd values come from the backend in store-local time;
  // when parsed naively (no T-component) the JS Date constructor reads
  // them as UTC midnight, which can flip the calendar day vs. store-
  // local. Construct from the parts directly to keep "2026-06-15" →
  // June 15 regardless of the user's tz.
  const [y, m, d] = yyyymmdd.split('-').map((s) => parseInt(s, 10))
  if (!y || !m || !d) return yyyymmdd
  const today = new Date()
  const yyyy = today.getFullYear()
  const mm = today.getMonth() + 1
  const dd = today.getDate()
  if (y === yyyy && m === mm && d === dd) return 'Today'
  const tomorrow = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1)
  if (
    y === tomorrow.getFullYear()
    && m === tomorrow.getMonth() + 1
    && d === tomorrow.getDate()
  ) {
    return 'Tomorrow'
  }
  return new Date(y, m - 1, d).toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  })
}

interface DayOption {
  value: string
  label: string
  open: boolean
}

// Fallback when the hint doesn't carry `days` (older backends or bare
// storefronts with no schedule). Next 7 days from today — every entry
// assumed open since we have no schedule data to say otherwise. The
// agent's schedule_order tool catches any bad picks on submit.
const generateDayOptions = (): DayOption[] => {
  const today = new Date()
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(today.getFullYear(), today.getMonth(), today.getDate() + i)
    const value = `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`
    return { value, label: dayLabel(value), open: true }
  })
}

const dayOptionsFromHint = (
  days: Array<{ value: string; open: boolean }> | undefined,
): DayOption[] => {
  if (!days || days.length === 0) return generateDayOptions()
  return days.map((d) => ({
    value: d.value,
    label: d.open ? dayLabel(d.value) : `(closed) ${dayLabel(d.value)}`,
    open: d.open,
  }))
}

// First open day in the list — used to default the picker so the user
// never lands on a closed day. Falls back to the first option (open or
// not) when nothing's open, so the select always has a value.
const firstOpenValue = (options: DayOption[]): string | undefined =>
  options.find((d) => d.open)?.value ?? options[0]?.value

const scheduleTimeOptions = (): Array<{ value: string; label: string }> =>
  Array.from({ length: (21 - 9) * 2 + 1 }, (_, i) => {
    const mins = 9 * 60 + i * 30
    const h = Math.floor(mins / 60)
    const m = mins % 60
    return {
      value: `${pad2(h)}:${pad2(m)}`,
      label: `${h % 12 === 0 ? 12 : h % 12}:${pad2(m)} ${h < 12 ? 'AM' : 'PM'}`,
    }
  })

@customElement('bb-chat-scheduled-time')
export class BbChatScheduledTime extends LitElement {
  static styles = css`${unsafeCSS(sheet)}`

  @property({ attribute: false }) hint!: ScheduledTimeConfirmHint
  @property({ type: Boolean }) disabled = false
  @property({ type: Boolean }) active = true
  @property({ reflect: true }) layout: 'inline' | 'stacked' = 'inline'

  @state() private _busy = false
  // User's current pick — bound to the <select> elements via .value so
  // a hint change can force-clear a stale pick. Without this, the
  // browser keeps the post-user-interaction value across re-renders and
  // a `?selected` flip on the options has no effect; user keeps re-
  // submitting the same rejected day. Trello #130.
  @state() private _day = ''
  @state() private _time = ''

  willUpdate(changed: Map<string, unknown>) {
    if (changed.has('hint')) {
      this._busy = false
      // New hint id → re-derive defaults from the (possibly updated)
      // suggested_iso. Snap to an open day even if the user had picked
      // a closed one — visible-but-disabled options are still in the
      // dropdown (Trello #130) but they can never be the default.
      const [suggDay, suggTime] = (this.hint.context.suggested_iso ?? '').split('T')
      const days = dayOptionsFromHint(this.hint.context.days)
      const times = scheduleTimeOptions()
      const matching = days.find((d) => d.value === suggDay)
      this._day = matching?.open
        ? suggDay
        : (firstOpenValue(days) ?? '')
      this._time = times.some((t) => t.value === suggTime?.slice(0, 5))
        ? suggTime!.slice(0, 5)
        : times[0].value
    }
    if (changed.has('disabled')) this._busy = false
  }

  render() {
    const off = this.disabled || !this.active || this._busy
    const days = dayOptionsFromHint(this.hint.context.days)
    const times = scheduleTimeOptions()
    // Pick a default that's always selectable: prefer the user's
    // current pick if it's open, otherwise the first open day, otherwise
    // the first option (degenerate case — nothing open in the horizon).
    const userPick = days.find((d) => d.value === this._day)
    const day = userPick?.open
      ? userPick.value
      : firstOpenValue(days) ?? days[0]?.value
    const time = times.some((t) => t.value === this._time)
      ? this._time
      : times[0]?.value
    const suggestedLabel = this.hint.context.suggested_label
    return html`
      <div class="sched" part="sched">
        <div class="sched-title" part="title">Schedule your order</div>
        ${suggestedLabel
          ? html`<div class="sched-suggestion" part="suggestion">
              Earliest available: ${suggestedLabel}
            </div>`
          : nothing}
        <label class="sched-field" part="field field-day">
          <span part="label label-day">Day</span>
          <select
            class="sched-day"
            part="select select-day"
            ?disabled=${off}
            .value=${day ?? ''}
            @change=${(e: Event) => {
              this._day = (e.target as HTMLSelectElement).value
            }}
          >
            ${days.map(
              (d) => html`<option
                value=${d.value}
                ?selected=${d.value === day}
                ?disabled=${!d.open}
              >${d.label}</option>`,
            )}
          </select>
        </label>
        <label class="sched-field" part="field field-time">
          <span part="label label-time">Time</span>
          <select
            class="sched-time"
            part="select select-time"
            ?disabled=${off}
            .value=${time ?? ''}
            @change=${(e: Event) => {
              this._time = (e.target as HTMLSelectElement).value
            }}
          >
            ${times.map(
              (t) =>
                html`<option value=${t.value} ?selected=${t.value === time}>${t.label}</option>`,
            )}
          </select>
        </label>
        <button
          class="schedule"
          part="cta cta-schedule"
          ?disabled=${off}
          @click=${this._onSchedule}
        >
          Schedule order
        </button>
        <button
          class="decline"
          part="cta cta-decline"
          ?disabled=${off}
          @click=${this._onDecline}
        >
          No thanks
        </button>
      </div>
    `
  }

  private _onSchedule = () => {
    if (this.disabled || !this.active || this._busy) return
    if (!this._day || !this._time) return
    const iso = `${this._day}T${this._time}`
    this._busy = true
    this._emitAffordance('scheduled-time-pick', { iso })
    this.dispatchEvent(
      new CustomEvent('schedule-order', {
        detail: { iso },
        bubbles: true,
        composed: true,
      }),
    )
  }

  private _onDecline = () => {
    if (this.disabled || !this.active || this._busy) return
    this._busy = true
    this._emitAffordance('scheduled-time-decline')
    this.dispatchEvent(
      new CustomEvent('schedule-decline', {
        bubbles: true,
        composed: true,
      }),
    )
  }

  private _emitAffordance(kind: string, context?: Record<string, unknown>) {
    this.dispatchEvent(
      new CustomEvent('widget-affordance', {
        detail: { kind, ...(context ? { context } : {}) },
        bubbles: true,
        composed: true,
      }),
    )
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'bb-chat-scheduled-time': BbChatScheduledTime
  }
}
